import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import api from "services/api";
import { RatesV2Api } from "services/generated";
import { handleErrors } from "services/helpers";
import { rootApi } from "store/root-api";
import appUrls from "utils/app-urls";
import { signalRService } from "utils/signalr";
import { useGetProfileQuery } from "./profile-api";
import { PriceChangedMessage } from "./types/PriceChangedMessage";
import { Rate } from "./types/Rate";
const API_URL = appUrls.getEndpointUrl();

const ratesClient = new RatesV2Api(
    undefined,
    new URL(API_URL, process.env.REACT_APP_API_URL).href,
    api,
);

const ratesAdapter = createEntityAdapter<Rate, string>({ selectId: (rate) => rate.baseProductCode + "_" + rate.quoteProductCode })

const ratesApi = rootApi.injectEndpoints({
    endpoints: builder => ({
        getRates: builder.query<EntityState<Rate, string>, { displayCurrency?: string }>({
            queryFn: async ({ displayCurrency }) => {
                const response = await handleErrors(() => ratesClient.getAvailableProductsRates(displayCurrency));
                if (response.data) {
                    // TODO: ideally we should get the timestamp from the response
                    const now = new Date().toISOString();
                    const mappedRates = Object.keys(response.data).map(key => {
                        const value = response.data[key];
                        const marketSplit = key.split("_");
                        return {
                            baseProductCode: marketSplit[0],
                            quoteProductCode: marketSplit[1],
                            price: value,
                            timestamp: now,
                        }
                    });
                    return { data: ratesAdapter.addMany(ratesAdapter.getInitialState(), mappedRates) }
                }
                return { error: response.error }
            },
            keepUnusedDataFor: 60 * 1000,
            onCacheEntryAdded: async (arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) => {
                try {
                    await cacheDataLoaded;
                    await signalRService.subscribeEvent<PriceChangedMessage>("PriceUpdateMessage", (data) => {
                        updateCachedData((draft) => {
                            const id = data.baseProduct + "_" + data.quoteProduct;

                            // NOTE: we use update instead of upsert here because we might receive price updates for assets
                            // we are not interested in, in which case we don't want to add them to the store
                            const timestamp = new Date(data.timestamp).toISOString();
                            ratesAdapter.updateOne(draft, { id, changes: { price: data.price, timestamp } });
                        });
                    });
                } catch (error) {
                    console.error(error)
                }

                await cacheEntryRemoved;
                await signalRService.unsubscribeEvent("PriceUpdateMessage");
            },
        }),
    }),
});

export function useGetRate(productCode?: string, displayCurrency?: string) {
    const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery();
    return useGetRatesQuery({ displayCurrency }, {
        // We use selectFromResult for performance optimization. This hook will only cause a rerender when the relevant product holdings change
        selectFromResult: ({ currentData, isLoading }) => ({
            data: currentData == null ? null : Object.entries(currentData.entities).find(([key, value]) => key.toUpperCase() == `${productCode?.toUpperCase()}_${(displayCurrency ?? profile?.displayCurrency?.code)?.toUpperCase()}`)?.[1],
            isLoading: isLoading || isProfileLoading
        }),
        skip: !productCode || !profile
    });
}

export const { useGetRatesQuery } = ratesApi;